<template>
  <div class="ad-activity">
    <p class="ad-activity__title mb-0">Activity</p>
    <v-divider></v-divider>

    <!-- draft: the record has no history until it exists on the server -->
    <div v-if="!assignmentId" class="ad-activity__body">
      <div class="ad-activity__placeholder">
        <div>
          <p class="text-center mb-0">There is no activity yet.</p>
          <p class="text-center mb-0">Publish the assignment to view</p>
          <p class="text-center mb-0">its history and write comments.</p>
        </div>
      </div>
    </div>

    <!-- history feed: oldest at the top, newest at the bottom, older pages pulled
         in as the user scrolls back up — the same flow as the task form -->
    <div v-else class="ad-activity__body" ref="feed" @scroll="handleScroll">
      <div v-if="loading" class="ad-activity__placeholder">
        <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
      </div>

      <div v-else-if="error" class="ad-activity__placeholder">
        <div>
          <p class="text-center mb-0">{{ errorMessage }}</p>
          <p
            v-if="canRetry"
            class="text-center mb-0 ad-activity__placeholder--action"
            @click="loadFirstPage"
          >
            Try again
          </p>
        </div>
      </div>

      <div v-else-if="!groups.length" class="ad-activity__placeholder">
        <div>
          <p class="text-center mb-0">No activity has been recorded</p>
          <p class="text-center mb-0">for this assignment yet.</p>
        </div>
      </div>

      <template v-else>
        <div v-if="loadingOlder" class="text-center py-2">
          <v-progress-circular indeterminate color="primary" size="22"></v-progress-circular>
        </div>

        <template v-for="group in groups">
          <!-- somebody wrote this: a card of its own, the way the task form
               renders a COMMENT row -->
          <div
            v-if="group.type === 'comment'"
            :key="group.key"
            class="ad-activity__comment"
          >
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center">
                <v-avatar :color="getColorForInitials(group.initials)" size="36">
                  <span class="white--text">{{ group.initials }}</span>
                </v-avatar>
                <p class="ad-activity__comment--author mb-0 ml-2">
                  {{ group.performedBy }}
                </p>
              </div>
              <p class="ad-activity__entry--timestamp mb-0">{{ group.performedAt }}</p>
            </div>
            <p class="ad-activity__comment--body mb-0">{{ group.body }}</p>
          </div>

          <!-- the record changed: one card per save, bulleted -->
          <div v-else :key="group.key" class="ad-activity__entry">
            <div class="d-flex align-center justify-space-between w-100">
              <p class="ad-activity__entry--author mb-0">{{ group.performedBy }}</p>
              <p class="ad-activity__entry--timestamp mb-0">{{ group.performedAt }}</p>
            </div>
            <div
              v-for="(line, index) in group.lines"
              :key="index"
              class="d-flex align-start mt-1"
            >
              <div class="ad-activity__entry--dot"></div>
              <p class="ad-activity__entry--text mb-0" :title="line.title">
                {{ line.text }}
              </p>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- Composer. Only on a saved assignment — a draft has nothing to attach a
         comment to, same rule the task form applies. The message is posted
         against the parent task, so it is shared with that task and its sibling
         assignments; an assignment with no parent task has nowhere to post and
         gets a read-only panel. -->
    <template v-if="assignmentId">
      <v-divider></v-divider>
      <div class="ad-activity__composer">
        <div class="ad-activity__composer--field-wrap">
          <v-textarea
            v-model="newComment"
            class="ad-activity__composer--field"
            :rows="composerRows"
            :disabled="!commentsEnabled || sending"
            :label="commentsEnabled ? 'Write a comment...' : 'Comments are not available for this assignment'"
            outlined
            solo
            flat
            hide-details
            color="#00000061"
            @focus="composerFocused = true"
            @blur="composerFocused = false"
          ></v-textarea>
          <button
            class="ad-activity__composer--send"
            :class="{ 'ad-activity__composer--send--active': canSend }"
            :disabled="!canSend"
            @click="sendComment"
          >
            {{ sending ? 'Sending…' : 'Send' }}
          </button>
        </div>
        <p v-if="!commentsEnabled" class="ad-activity__composer--hint mb-0">
          This assignment has no parent task, so there is nowhere to post a
          comment.
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import {
  getAssignmentActivities,
  getTaskComments,
  processTaskComment,
} from "@/utils/newDbUtils.js";
import { OFFICE_TIME_ZONE } from "@/utils/assignmentFormUtils.js";
import { alphabetColors } from "@/utils/constants.js";

// Rows per request. The API's own default, kept explicit so the paging maths
// below reads plainly.
const PAGE_SIZE = 25;
// /task-activity's own default page size.
const COMMENT_PAGE_SIZE = 20;

// Comments render as their own card rather than as another line of audit trail —
// the task form's COMMENT / ACTION split, in the vocabulary this feed uses.
const COMMENT_ACTIVITY_TYPE = "COMMENT";

// Composer growth, matching the task form: one row at rest, then roughly a row
// per 60 characters plus any hard line breaks, up to a cap.
const COMPOSER_CHARS_PER_ROW = 60;
const COMPOSER_BASE_ROWS = 3;
const COMPOSER_MAX_ROWS = 12;

// The activity types that describe the record as a whole and carry no field
// values of their own.
const STANDALONE_LABELS = {
  CREATED: "Created this assignment",
  ARCHIVED: "Archived this assignment",
  RESTORED: "Restored this assignment",
};

// Longer than this and a bullet stops being scannable, so the row shows the head
// of the value and the tooltip carries the whole of it. History values are stored
// truncated at 2000 characters, which is far past readable in a 420px panel.
const MAX_VALUE_LENGTH = 160;

export default {
  name: "ADFormActivity",

  props: {
    // The saved assignment's record id. Null on a draft, which is what the
    // "no activity yet" state is for.
    assignmentId: {
      type: [String, Number],
      default: null,
    },
    // Record id of the parent task. The conversation lives on the task — there is
    // no assignment-scoped comment route — so this is what the composer posts
    // against and what the comment feed is read from.
    taskDbId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      // Oldest first, i.e. display order — both APIs answer newest first.
      rows: [],
      // Highest page index fetched from each source. They are paged in step and
      // the accumulated rows re-sorted, so the two never have to line up.
      activityPage: 0,
      activityTotalPages: 0,
      commentPage: 0,
      commentTotalPages: 0,
      loading: false,
      loadingOlder: false,
      error: false,
      errorMessage: "",
      canRetry: true,
      // Bumped on every reload. `assignmentId` and `taskDbId` land in separate
      // ticks when the form adopts a record, so more than one load can be in
      // flight at once; a response whose token is stale is discarded rather than
      // overwriting a newer one.
      loadToken: 0,
      // Composer.
      newComment: "",
      composerFocused: false,
      sending: false,
    };
  },

  computed: {
    // Either source having another page keeps the scroll-back alive.
    hasOlderPages() {
      return (
        this.activityPage < this.activityTotalPages - 1 ||
        this.commentPage < this.commentTotalPages - 1
      );
    },
    // Commenting needs a parent task to hang the message on. A saved assignment
    // that somehow has none gets a read-only panel rather than a Send that cannot
    // work.
    commentsEnabled() {
      return !!this.taskDbId;
    },
    // One card per save. The API writes a row per changed field, so rows sharing
    // a performer and a timestamp are one action and read as a single bulleted
    // entry — the shape the task form's activity cards use. Comments never merge:
    // each is somebody talking, not a field changing.
    groups() {
      const groups = [];
      this.rows.forEach((row) => {
        if (row.activityType === COMMENT_ACTIVITY_TYPE) {
          const author = row.performedByName || "System";
          groups.push({
            key: `comment|${row.id}`,
            type: "comment",
            performedBy: author,
            performedAt: this.formatTimestamp(row.performedAt),
            initials: this.getInitials(author),
            body: this.commentBody(row),
          });
          return;
        }

        const groupKey = `${row.performedById ?? "system"}|${row.performedAt ?? ""}`;
        const last = groups[groups.length - 1];
        if (last && last.type === "history" && last.groupKey === groupKey) {
          last.lines.push(this.describe(row));
          return;
        }
        groups.push({
          // The same person saving twice in the same second would repeat the
          // key, so the first row's id makes it unique for :key. `groupKey` is
          // what merging compares — comparing against `key` never matched,
          // which is what used to give every field its own card.
          key: `${groupKey}|${row.id}`,
          groupKey,
          type: "history",
          // A scheduled job or an import leaves no performer behind.
          performedBy: row.performedByName || "System",
          performedAt: this.formatTimestamp(row.performedAt),
          lines: [this.describe(row)],
        });
      });
      return groups;
    },
    composerRows() {
      if (!this.composerFocused && !this.newComment) return 1;
      const lineBreaks = (this.newComment.match(/\n/g) || []).length;
      const wrapped = Math.floor(this.newComment.length / COMPOSER_CHARS_PER_ROW);
      return Math.min(
        wrapped + lineBreaks + COMPOSER_BASE_ROWS,
        COMPOSER_MAX_ROWS
      );
    },
    canSend() {
      return this.commentsEnabled && !this.sending && !!this.newComment.trim();
    },
  },

  watch: {
    assignmentId() {
      this.loadFirstPage();
    },
    // The parent task arrives a tick after the assignment on load, and changes
    // when the hierarchy panel moves to a sibling.
    taskDbId() {
      this.loadFirstPage();
    },
  },

  methods: {
    ...mapActions("flashMessage", ["handleFlash"]),
    async loadFirstPage() {
      if (!this.assignmentId) return;
      const token = ++this.loadToken;
      this.loading = true;
      this.error = false;
      this.errorMessage = "";
      this.rows = [];
      this.activityPage = 0;
      this.activityTotalPages = 0;
      this.commentPage = 0;
      this.commentTotalPages = 0;

      // Two independent sources: the assignment's own field history, and the
      // conversation on its parent task. Fetched together so the first paint has
      // both.
      const [activityRows, commentRows] = await Promise.all([
        this.fetchActivityPage(0),
        this.fetchCommentPage(0),
      ]);

      // A newer load started while these were in flight; that one owns the panel.
      if (token !== this.loadToken) return;

      if (activityRows === null) {
        // The history is the panel's primary content — if it fails there is
        // nothing meaningful to show, so this is the state the user sees.
        this.loading = false;
        return;
      }

      this.rows = this.mergeRows([...activityRows, ...(commentRows || [])]);
      this.loading = false;
      // The newest entry sits at the bottom, so that is where the panel opens.
      this.$nextTick(this.scrollToBottom);
    },

    async loadOlderPage() {
      if (this.loading || this.loadingOlder || !this.hasOlderPages) return;
      this.loadingOlder = true;

      const token = this.loadToken;
      const feed = this.$refs.feed;
      const heightBefore = feed ? feed.scrollHeight : 0;

      // Step both sources on together. Whichever is exhausted just contributes
      // nothing; `mergeRows` re-sorts everything loaded so far, so the two page
      // cursors never have to stay aligned.
      const wants = [];
      const nextActivityPage = this.activityPage + 1;
      const nextCommentPage = this.commentPage + 1;
      const activityHasMore = this.activityPage < this.activityTotalPages - 1;
      const commentHasMore = this.commentPage < this.commentTotalPages - 1;

      wants.push(
        activityHasMore ? this.fetchActivityPage(nextActivityPage) : []
      );
      wants.push(commentHasMore ? this.fetchCommentPage(nextCommentPage) : []);

      const [activityRows, commentRows] = await Promise.all(wants);

      // The panel was reloaded from the top underneath this scroll-back; its rows
      // are the current ones and appending an older page to them would be wrong.
      if (token !== this.loadToken) {
        this.loadingOlder = false;
        return;
      }

      if (activityHasMore) {
        if (activityRows === null) {
          // What is already on screen stays there — a failed older page must not
          // replace a readable history with an error. Treating it as the end of
          // the list is what stops the next scroll from retrying forever.
          this.activityTotalPages = nextActivityPage;
        } else {
          this.activityPage = nextActivityPage;
        }
      }
      if (commentHasMore) {
        if (commentRows === null) this.commentTotalPages = nextCommentPage;
        else this.commentPage = nextCommentPage;
      }

      this.rows = this.mergeRows([
        ...this.rows,
        ...(activityRows || []),
        ...(commentRows || []),
      ]);
      this.loadingOlder = false;

      // Keep the row the user was reading under the cursor.
      this.$nextTick(() => {
        if (!feed) return;
        feed.scrollTop = feed.scrollHeight - heightBefore;
      });
    },

    // One page of assignment history. Returns null on failure; the caller decides
    // whether that is fatal.
    async fetchActivityPage(page) {
      const resp = await getAssignmentActivities(this.assignmentId, {
        page,
        size: PAGE_SIZE,
      });
      if (!resp || resp.status > 204 || !resp.data) {
        if (page === 0) this.applyError(resp);
        else
          console.error(
            "Failed to load an older page of assignment activity:",
            resp?.status
          );
        return null;
      }
      this.activityTotalPages = resp.data.totalPages ?? 0;
      return resp.data.content || [];
    },

    // One page of the parent task's conversation, normalised into the shape the
    // feed already renders. Only COMMENT rows are taken: the task's own ACTION
    // rows describe the task changing, which would read as noise next to this
    // assignment's field history.
    //
    // Never fatal — a conversation that cannot be read must not hide the history.
    async fetchCommentPage(page) {
      if (!this.taskDbId) return [];
      const resp = await getTaskComments(this.taskDbId, {
        page,
        size: COMMENT_PAGE_SIZE,
      });
      if (!resp || resp.status > 204 || !resp.data) {
        console.error("Failed to load task comments:", resp?.status);
        return null;
      }
      this.commentTotalPages = resp.data.totalPages ?? 0;
      return (resp.data.content || [])
        .filter((row) => row.commentType === COMMENT_ACTIVITY_TYPE)
        .map((row) => this.normalizeTaskComment(row));
    },

    // /task-activity speaks commentBy / commentDateTime; the feed speaks
    // performedByName / performedAt. Translated here so `groups`, the timestamp
    // formatting and the comment card all work unchanged.
    normalizeTaskComment(row) {
      const author = [row.commentBy?.firstName, row.commentBy?.lastName]
        .filter(Boolean)
        .join(" ");
      return {
        // Prefixed so a task comment and an activity row can never collide on
        // the :key the feed builds from this.
        id: `task-comment-${row.id}`,
        activityType: COMMENT_ACTIVITY_TYPE,
        performedByName: author || "System",
        performedById: row.commentBy?.id ?? null,
        performedAt: row.commentDateTime,
        comment: row.comment,
      };
    },

    // Display order is oldest first. Sorting the whole accumulated set on every
    // load — rather than prepending each page — is what keeps two sources paged at
    // different rates in one correct chronological sequence. Duplicates are
    // dropped so a reload after sending cannot double a row up.
    mergeRows(rows) {
      const seen = new Set();
      return rows
        .filter((row) => {
          const key = `${row.activityType}|${row.id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .sort((a, b) => {
          const at = this.toDate(a.performedAt)?.getTime() ?? 0;
          const bt = this.toDate(b.performedAt)?.getTime() ?? 0;
          if (at !== bt) return at - bt;
          // Same instant: keep the API's own ordering stable rather than letting
          // a save's field rows shuffle between renders.
          return String(a.id).localeCompare(String(b.id));
        });
    },

    applyError(resp) {
      this.error = true;
      if (resp?.status === 403) {
        this.errorMessage = "You don't have access to this assignment's history.";
        this.canRetry = false;
      } else if (resp?.status === 404) {
        this.errorMessage = "No history was found for this assignment.";
        this.canRetry = false;
      } else {
        this.errorMessage = "The activity could not be loaded.";
        this.canRetry = true;
      }
    },

    // Posts the composer's contents against the parent task, the same call the
    // task form's composer makes. `commentById` is null because the server takes
    // the author from the token.
    //
    // The field is cleared only once the post resolves, so a failure leaves what
    // was typed in place to retry rather than losing it.
    async sendComment() {
      if (!this.canSend) return;
      const comment = this.newComment.trim();

      this.sending = true;
      try {
        const resp = await processTaskComment({
          taskId: this.taskDbId,
          comment,
          commentById: null,
          commentType: COMMENT_ACTIVITY_TYPE,
        });
        // A request that never reached the server resolves without a response, so
        // this tests for a success rather than for a failure.
        if (!resp || resp.status > 204) {
          console.error("Failed to post the comment:", resp?.status);
          // Reported through the global flash, the way the rest of the form
          // reports a failed write — the panel has no alert of its own, and
          // silently leaving the text in the box reads as nothing happening.
          this.handleFlash({
            response: {
              data: {
                statusCode: 500,
                error:
                  resp?.data?.message ||
                  resp?.data?.error ||
                  "Failed to post the comment. Please try again.",
              },
            },
            show: true,
          });
          return;
        }
        this.newComment = "";
        // Reloaded rather than pushed in locally, so the row arrives with the
        // server's own id and timestamp and sorts where it belongs.
        await this.loadFirstPage();
        this.$nextTick(this.scrollToBottom);
      } finally {
        this.sending = false;
      }
    },

    // A comment's text. Read off `comment` first and `newValue` second because
    // the endpoint that will carry these does not exist yet — whichever field
    // it settles on, the feed renders.
    commentBody(row) {
      return row.comment ?? row.newValue ?? "";
    },

    getInitials(name) {
      return String(name || "")
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    },

    // Same convention as the rest of the form: the second letter picks the
    // colour, with a fallback for one-word names.
    getColorForInitials(initials) {
      return (
        (initials &&
          initials[1] &&
          alphabetColors.find((color) => color.character === initials[1])
            ?.color) ||
        "#9747FF"
      );
    },

    handleScroll(event) {
      if (event.target.scrollTop <= 0) this.loadOlderPage();
    },

    scrollToBottom() {
      const feed = this.$refs.feed;
      if (!feed) return;
      feed.scrollTop = feed.scrollHeight;
    },

    // One bullet: what changed, and the untruncated value for the tooltip.
    describe(row) {
      const label = row.fieldLabel || row.field || "";

      switch (row.activityType) {
        case "CREATED":
        case "ARCHIVED":
        case "RESTORED":
          return { text: STANDALONE_LABELS[row.activityType], title: "" };
        case "STAFF_ASSIGNED":
          return {
            text: `${label} assigned: ${this.formatValue(row.newValue)}`,
            title: "",
          };
        case "STAFF_UNASSIGNED":
          return {
            text: `${label} unassigned: ${this.formatValue(row.oldValue)}`,
            title: "",
          };
        default: {
          // A type this build doesn't know about still has to render as
          // something — never a blank row.
          if (!label && row.oldValue == null && row.newValue == null)
            return { text: this.prettifyType(row.activityType), title: "" };

          const oldValue = this.formatValue(row.oldValue);
          const newValue = this.formatValue(row.newValue);
          const full = `${label}: ${oldValue} → ${newValue}`;
          const text = `${label}: ${this.clamp(oldValue)} → ${this.clamp(newValue)}`;
          return { text, title: text === full ? "" : full };
        }
      }
    },

    prettifyType(activityType) {
      const words = String(activityType || "CHANGED").replace(/_/g, " ").toLowerCase();
      return words.charAt(0).toUpperCase() + words.slice(1);
    },

    clamp(value) {
      const text = String(value);
      return text.length > MAX_VALUE_LENGTH
        ? `${text.slice(0, MAX_VALUE_LENGTH)}…`
        : text;
    },

    // History values are stored as text in a machine-readable form (ISO dates,
    // plain-string decimals, true/false), so they are read back into something
    // displayable here.
    formatValue(value) {
      if (value === null || value === undefined || value === "") return "—";

      const raw = String(value);
      if (raw === "true") return "Yes";
      if (raw === "false") return "No";

      // 2026-08-14, optionally with a time. Rendered in UTC on purpose: the
      // stored value has no zone, so shifting it would show the user a date
      // nobody ever typed.
      const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?$/);
      if (isoDate) {
        const [, year, month, day, hour, minute] = isoDate;
        const date = new Date(
          Date.UTC(+year, +month - 1, +day, hour ? +hour : 0, minute ? +minute : 0)
        );
        const options = {
          day: "2-digit",
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        };
        if (hour)
          Object.assign(options, {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        return this.tidyDateString(
          new Intl.DateTimeFormat("en-US", options).format(date)
        );
      }

      // 17:00:00 → 5:00pm
      const isoTime = raw.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
      if (isoTime) {
        const hour = Number(isoTime[1]);
        const suffix = hour < 12 ? "am" : "pm";
        return `${hour % 12 === 0 ? 12 : hour % 12}:${isoTime[2]}${suffix}`;
      }

      // 120.0 → 120, and 0.125 is left alone.
      if (/^-?\d+\.\d+$/.test(raw)) return String(Number(raw));

      return raw;
    },

    // Three shapes reach this: a [year, month, day, hour, minute] array (what the
    // task endpoints send), a bare epoch number, or an ISO-8601 string.
    toDate(value) {
      if (!value) return null;

      if (Array.isArray(value)) {
        const [year, month, day, hour = 0, minute = 0] = value;
        return new Date(Date.UTC(year, month - 1, day, hour, minute));
      }

      // This endpoint sends epoch *seconds* — Jackson's default for an Instant,
      // and fractional when it carries sub-second precision. `new Date` reads a
      // lone number as milliseconds, so seconds were landing in January 1970.
      // Any date this app shows is ~1e9 in seconds and ~1e12 in milliseconds, so
      // there is a wide gap to separate them on.
      const epoch =
        typeof value === "number"
          ? value
          : /^\d+(\.\d+)?$/.test(String(value))
          ? Number(value)
          : null;
      if (epoch !== null) {
        const date = new Date(epoch < 1e11 ? epoch * 1000 : epoch);
        return Number.isNaN(date.getTime()) ? null : date;
      }

      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    },

    // Office time, in the same format the task form's activity stamps use.
    formatTimestamp(value) {
      const date = this.toDate(value);
      if (!date) return "";
      return this.tidyDateString(
        new Intl.DateTimeFormat("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: OFFICE_TIME_ZONE,
        }).format(date)
      );
    },

    tidyDateString(formatted) {
      return formatted.replace(",", "").replace(" AM", "am").replace(" PM", "pm");
    },
  },

  mounted() {
    this.loadFirstPage();
  },
};
</script>

<style lang="scss" scoped>
.ad-activity {
  font-family: 'Roboto', sans-serif !important;
  background-color: #fff;
  // Matches the hierarchy panel and the details column, so the three end level
  // and the modal itself never needs a scrollbar — the feed scrolls inside its
  // own body.
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__title {
    font-size: 20px;
    font-weight: 400;
    line-height: 26px;
    color: #656F7D;
    padding: 16px 20px;
  }

  &__body {
    // The panel's only scroll container.
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 10px 7px 20px 7px;
    background-color: #FBFBFC;

    &::-webkit-scrollbar {
      width: 10px;
    }
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    &::-webkit-scrollbar-thumb {
      background: #ADB3BD;
      border-radius: 8px;
    }
    & {
      scrollbar-width: thin;
      scrollbar-color: #ADB3BD #f1f1f1;
    }
  }

  &__placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    & p {
      font-family: Roboto;
      font-size: 16px;
      font-weight: 400;
      line-height: 18px;
      text-align: center;
      color: #B4B4B4;
    }
    &--action {
      color: #1976D2 !important;
      margin-top: 8px !important;
      &:hover {
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }

  &__entry {
    padding: 5px 13px;
    margin-bottom: 10px;

    &--author {
      font-size: 12px;
      font-weight: 400;
      color: #949494;
    }

    &--timestamp {
      font-size: 12px;
      font-weight: 500;
      color: #949494;
      white-space: nowrap;
      padding-left: 8px;
    }

    &--dot {
      width: 3px;
      height: 3px;
      background-color: #000;
      border-radius: 50%;
      margin-right: 7px;
      margin-top: 8px;
      flex-shrink: 0;
    }

    &--text {
      font-size: 14px;
      line-height: 20px;
      font-weight: 300;
      color: #000;
      // A history value can be a URL or an unbroken run of text; it wraps rather
      // than widening the panel.
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }

  // A written message, boxed so it reads as somebody speaking rather than as
  // another line of audit trail — the same distinction the task form draws.
  &__comment {
    background-color: #fff;
    border: 1px solid #E8EAED;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 10px;

    &--author {
      font-size: 16px;
      font-weight: 500;
      color: #000;
    }

    &--body {
      margin-top: 10px;
      font-size: 14px;
      line-height: 20px;
      font-weight: 300;
      color: #000;
      // Keeps the line breaks the author typed, and never widens the panel.
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }

  // Sits below the feed as a fixed-height flex child, so growing the textarea
  // takes room from the feed instead of pushing the panel past its 80vh.
  &__composer {
    flex: 0 0 auto;
    padding: 10px;
    background-color: #fff;

    &--field-wrap {
      position: relative;
    }

    &--field {
      ::v-deep textarea {
        resize: none !important;
        font-family: 'Roboto', sans-serif !important;
        font-size: 14px !important;
        font-weight: 300 !important;
        line-height: 18px !important;
      }
      // Room for the Send button, which floats over the field's bottom-right.
      ::v-deep .v-input__control .v-input__slot {
        padding-bottom: 50px !important;
      }
    }

    &--send {
      position: absolute;
      right: 12px;
      bottom: 11px;
      z-index: 2;
      padding: 10px 20px;
      border-radius: 4px;
      // Muted until there is something to send; the active rule below is the
      // only thing that brightens it.
      background-color: #6ab3fc80;
      color: #FFFFFF;
      font-size: 16px;
      font-weight: 700;
      line-height: 18px;
      text-transform: uppercase;

      &--active {
        background-color: #1976D2;
        &:hover {
          cursor: pointer;
        }
      }

      &:disabled {
        cursor: not-allowed;
      }
    }

    &--hint {
      margin-top: 8px;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      color: #949494;
    }
  }
}
</style>
